import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import styled from "styled-components";
import MetricsAPI from "@/api/Metrics/Metrics.js";
import { formatCurrency } from "@/utils/currency-formatter";
import { timezone } from "@/utils/timezoneResolver";
import CoreWidget from "./CoreWidget";

const TimeTrackedWidget = styled(({ className }) => {
  const metrics = [
    {
      metric: "timeTracked",
      args: [
        timezone,
        moment().startOf("month").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
      ],
    },
    {
      metric: "timeTracked",
      args: [
        timezone,
        moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      ],
    },
  ];

  const { data } = useQuery({
    queryKey: ["metrics", { metrics }],
    queryFn: () => MetricsAPI.getMetrics({ metrics }),
  });

  if (!data) return null;

  const timeTrackedThisMonth = data?.data?.[0]?.timeTracked || {};
  const timeTrackedLastMonth = data?.data?.[1]?.timeTracked || {};

  // calculate percentage change
  let percentageChange: number | string =
    ((timeTrackedThisMonth.time_tracked - timeTrackedLastMonth.time_tracked) /
      timeTrackedLastMonth.time_tracked) *
    100;

  percentageChange = percentageChange.toFixed(2);

  const timeTrackDetails = [
    {
      label: "Billed Hours",
      value: timeTrackedThisMonth.total_billed,
    },
    {
      label: "Billed Amount",
      value: formatCurrency(timeTrackedThisMonth?.total_billed_price || 0),
    },
    {
      label: "Un-Billed Hours",
      value: timeTrackedThisMonth?.total_unbilled || 0,
    },
    {
      label: "Un-billed Amount",
      value: formatCurrency(timeTrackedThisMonth?.total_unbilled_price || 0),
    },
  ];

  return (
    <CoreWidget
      statTitle="Time Tracked (This Month)"
      statValue={`${timeTrackedThisMonth?.time_tracked || 0} h`}
      percentageChange={percentageChange}
      detailItems={timeTrackDetails}
    />
  );
})`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 10px;

  .main-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;
    background-color: ${({ theme }) =>
      theme.name === "DARK" ? "#2d2d2d" : "#f5f5f5"};
    border-radius: 4px;

    .stat-title {
      font-size: 14px;
      font-weight: 600;
      color: ${({ theme }) => theme.palette.text.secondary};
      margin-bottom: 10px;
    }

    .stat-value {
      font-size: 18px;
      font-weight: 600;
      color: ${({ theme }) => theme.palette.text.primary};
    }

    .percent-change {
      font-size: 12px;
      font-weight: 400;

      &.positive {
        color: ${({ theme }) => theme.palette.success.main};
      }

      &.negative {
        color: ${({ theme }) => theme.palette.error.main};
      }
    }

    .percent-change::before {
      margin-left: 5px;
      margin-right: 2px;
    }

    .percent-change.positive::before {
      content: "▲";
    }

    .percent-change.negative::before {
      content: "▼";
    }
  }

  .stat-selector {
    position: absolute;
    top: 30px;
    right: 30px;

    svg {
      color: ${({ theme }) => theme.palette.text.secondary};
    }
  }

  .additional-content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(50px, auto));
    grid-gap: 10px;
    padding: 10px;

    .detail-item {
    }

    .detail-label {
      font-size: 0.75rem;
      font-weight: 300;
      color: ${({ theme }) => theme.palette.text.secondary};
      cursor: default;
    }

    .detail-value {
      user-select: text;
      font-size: 0.75rem;
      font-weight: 400;
      color: ${({ theme }) => theme.palette.text.primary};
      cursor: default;

      &.no-cap {
        text-transform: none;
      }
    }
  }
`;

export default TimeTrackedWidget;
